import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { JerseyBackPreview } from "@/components/JerseyBackPreview";

export default function BuildYourJersey() {
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");

  const updateName = (v) => setName(v.toUpperCase());
  const updateNumber = (v) => setNumber(v.replace(/[^0-9]/g, ""));

  const scrollShop = () => document.querySelector("#shop")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="build"
      data-testid="build-your-jersey"
      className="relative bg-background border-t border-white/10 px-6 md:px-12 lg:px-16 py-24 md:py-32 overflow-hidden"
    >
      <p className="pointer-events-none absolute -top-6 right-0 font-anton text-[22vw] lg:text-[13vw] leading-none uppercase text-white/[0.03] select-none">
        Yours
      </p>

      <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Left: copy + inputs */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.35em] font-bold text-primary mb-6">
            <Sparkles className="w-4 h-4" /> Free personalization
          </p>
          <h2 className="font-anton text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.85] text-white">
            Build your<br /><span className="text-primary">jersey.</span>
          </h2>
          <p className="text-white/60 mt-8 max-w-md leading-relaxed">
            Put your <span className="text-white font-semibold">name & number</span> on the back — free on every crest
            drop. The embroidered crest stays up front, your legacy goes on the back. Type it below and watch it come to
            life.
          </p>

          <div className="grid grid-cols-3 gap-3 mt-10 max-w-md">
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Your name</label>
              <input
                data-testid="build-name-input"
                type="text"
                maxLength={12}
                value={name}
                onChange={(e) => updateName(e.target.value)}
                placeholder="YOUR NAME"
                className="mt-1.5 bg-transparent border border-white/20 focus:border-primary outline-none p-3 w-full text-white uppercase text-sm transition-colors"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Number</label>
              <input
                data-testid="build-number-input"
                type="text"
                inputMode="numeric"
                maxLength={2}
                value={number}
                onChange={(e) => updateNumber(e.target.value)}
                placeholder="10"
                className="mt-1.5 bg-transparent border border-white/20 focus:border-primary outline-none p-3 w-full text-white text-sm transition-colors"
              />
            </div>
          </div>

          <button
            data-testid="build-shop-button"
            onClick={scrollShop}
            className="group mt-10 bg-primary text-black hover:bg-white px-8 py-4 font-bold uppercase tracking-[0.2em] text-sm transition-colors active:scale-95 flex items-center gap-3"
          >
            Pick your team
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>

        {/* Right: live jersey back preview */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="absolute -inset-4 border border-primary/20" />
          <div data-testid="build-preview" className="relative">
            <span className="absolute top-5 left-1/2 -translate-x-1/2 z-10 text-[10px] uppercase tracking-[0.3em] font-bold text-white/25">
              Back print
            </span>
            <JerseyBackPreview name={name} number={number} className="border border-white/10" />
          </div>
          <p className="text-[11px] text-white/40 mt-4 text-center uppercase tracking-[0.2em]">
            Live preview — printed exactly like this
          </p>
        </motion.div>
      </div>
    </section>
  );
}
